import React, { useState, useRef, useEffect, useCallback } from 'react';
import WinterMarkdown from './WinterMarkdown';
import { streamWinterReply } from '../utils/winterStream';

/**
 * Ask Winter — the in-app AI assistant.
 *
 * Floating bubble bottom-right. Click → slides up a chat panel.
 * Winter is a Samoyed dog persona rendered as inline SVG.
 *
 * 2026-05-29 overhaul ("state of the art" pass):
 *   - Streaming replies (SSE) with a live caret + Stop button
 *   - Tool-activity chips ("Searching call transcripts…") while
 *     Winter works — he can chain up to 6 tool calls per turn
 *   - Rich markdown (lists, tables, code, quotes) via WinterMarkdown
 *   - Copy button on replies, New-chat, sessionStorage persistence
 *   - Auto-growing composer, Enter to send / Shift+Enter newline
 *
 * Backed by POST /api/ask-winter/stream with automatic fallback to
 * the JSON endpoint (deploy skew / buffering proxies) — see
 * utils/winterStream.js.
 */

const WINTER_QUICK_PROMPTS = [
  'Which calls mentioned pricing?',
  "How are my trailers performing this week?",
  'What deals are in flight right now?',
  'How do I schedule a meeting bot?',
];

const WINTER_WELCOME =
  "Hi! I'm Winter. I know the product cold, and I can read your data live — demos, deals, engagement, even what was said on your calls. Ask me anything.";

// Session-scoped thread persistence — survives navigation + reload,
// clears when the tab closes (same policy as the call-detail tab).
const WINTER_THREAD_KEY = 'winter:thread:global:v2';

// Little Samoyed face rendered in inline SVG. Pure white fluff, a pink
// nose, button eyes, a tongue peek. Fits in a 56x56 circle and carries
// its own personality without a photo asset.
function SamoyedAvatar({ size = 56 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Winter the Samoyed"
      style={{ display: 'block' }}
    >
      <defs>
        <radialGradient id="winterFluff" cx="50%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e8ecf3" />
        </radialGradient>
        <radialGradient id="winterCheek" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffe0ec" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#ffe0ec" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background circle — soft sky so the white face reads on dark UI */}
      <circle cx="50" cy="50" r="50" fill="#a0c6f0" />

      {/* Ears (fluffy triangles) */}
      <path d="M20 35 Q18 18 32 24 L34 38 Z" fill="url(#winterFluff)" stroke="#d6dde7" strokeWidth="0.5" />
      <path d="M80 35 Q82 18 68 24 L66 38 Z" fill="url(#winterFluff)" stroke="#d6dde7" strokeWidth="0.5" />

      {/* Inner ears (pale pink) */}
      <path d="M23 30 Q22 22 30 27 L31 35 Z" fill="#ffd1dc" opacity="0.8" />
      <path d="M77 30 Q78 22 70 27 L69 35 Z" fill="#ffd1dc" opacity="0.8" />

      {/* Head (big fluffy circle) */}
      <circle cx="50" cy="55" r="32" fill="url(#winterFluff)" stroke="#d6dde7" strokeWidth="0.8" />

      {/* Cheek blush for cuteness */}
      <circle cx="32" cy="62" r="8" fill="url(#winterCheek)" />
      <circle cx="68" cy="62" r="8" fill="url(#winterCheek)" />

      {/* Eyes — happy Samoyed half-moons */}
      <path d="M38 50 Q41 47 44 50" stroke="#2b2b2b" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M56 50 Q59 47 62 50" stroke="#2b2b2b" strokeWidth="2.5" fill="none" strokeLinecap="round" />

      {/* Nose (little pink triangle) */}
      <path d="M47 60 Q50 57 53 60 Q52 63 50 63 Q48 63 47 60 Z" fill="#1a1a1a" />

      {/* Mouth + tongue peek */}
      <path d="M50 63 Q50 68 46 68" stroke="#2b2b2b" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M50 63 Q50 68 54 68" stroke="#2b2b2b" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <ellipse cx="50" cy="69" rx="3" ry="2" fill="#ff9bb3" />

      {/* Fluffy chin tuft */}
      <path
        d="M42 72 Q50 82 58 72 Q54 78 50 78 Q46 78 42 72 Z"
        fill="url(#winterFluff)"
        stroke="#d6dde7"
        strokeWidth="0.5"
      />
    </svg>
  );
}

// localStorage key for the first-time intro coachmark. Flipping this
// name resets the "has seen Winter intro" state for every user.
const WINTER_INTRO_SEEN_KEY = 'ask_winter_intro_seen_v1';

// Scoped styles — markdown body, animations, scrollbar. Injected once
// while the panel is open; kept here (not App.css) so AskWinter stays
// a self-contained drop-in component.
const WINTER_STYLES = `
  @keyframes winterIntroFade {
    from { opacity: 0; transform: translateX(8px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes winterBubblePulse {
    0%, 100% { box-shadow: 0 10px 30px rgba(0,0,0,0.35), 0 0 0 4px rgba(160,198,240,0.25); }
    50%      { box-shadow: 0 10px 30px rgba(0,0,0,0.35), 0 0 0 10px rgba(160,198,240,0.35); }
  }
  @keyframes winterPanelIn {
    from { opacity: 0; transform: translateY(14px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes winterMsgIn {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes winterCaretBlink {
    0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; }
  }
  @keyframes winterToolSpin {
    to { transform: rotate(360deg); }
  }
  .winter-panel { animation: winterPanelIn 200ms cubic-bezier(0.21, 1.02, 0.73, 1); }
  .winter-row { animation: winterMsgIn 160ms ease-out; }
  .winter-scroll::-webkit-scrollbar { width: 8px; }
  .winter-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 8px; }
  .winter-scroll::-webkit-scrollbar-track { background: transparent; }
  .winter-caret {
    display: inline-block; width: 7px; height: 15px; margin-left: 2px;
    vertical-align: -2px; background: #a0c6f0; border-radius: 1.5px;
    animation: winterCaretBlink 900ms step-end infinite;
  }
  .winter-tool-chip {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 5px 11px; border-radius: 999px;
    background: rgba(160,198,240,0.10); border: 1px solid rgba(160,198,240,0.25);
    color: #b9d3f2; font-size: 12px; font-weight: 600;
  }
  .winter-tool-chip .spin {
    width: 11px; height: 11px; border-radius: 50%; flex: none;
    border: 2px solid rgba(185,211,242,0.3); border-top-color: #b9d3f2;
    animation: winterToolSpin 700ms linear infinite;
  }
  .winter-copy-btn {
    position: absolute; top: 6px; right: 6px;
    padding: 3px 8px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.14);
    background: rgba(20,20,28,0.85); color: #cbd5e1;
    font-size: 11px; font-weight: 600; cursor: pointer;
    opacity: 0; transition: opacity 120ms ease;
  }
  .winter-msg-wrap:hover .winter-copy-btn { opacity: 1; }

  /* Markdown body — tuned for a 300px-wide dark bubble */
  .winter-md { font-size: 13.5px; line-height: 1.55; color: #e8ecf3; word-break: break-word; }
  .winter-md p { margin: 0 0 8px; }
  .winter-md p:last-child { margin-bottom: 0; }
  .winter-md ul, .winter-md ol { margin: 4px 0 8px; padding-left: 20px; }
  .winter-md li { margin: 2px 0; }
  .winter-md code {
    background: rgba(160,198,240,0.14); border-radius: 4px;
    padding: 1px 5px; font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }
  .winter-md pre {
    background: #0b0b12; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px; padding: 10px 12px; overflow-x: auto; margin: 6px 0 8px;
  }
  .winter-md pre code { background: none; padding: 0; font-size: 12px; }
  .winter-md a { color: #a0c6f0; text-decoration: underline; }
  .winter-md blockquote {
    margin: 6px 0 8px; padding: 4px 10px;
    border-left: 3px solid rgba(160,198,240,0.5);
    color: #c7d2e0; font-style: italic;
  }
  .winter-md hr { border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 8px 0; }
  .winter-md .wm-h { font-weight: 700; margin: 8px 0 4px; color: #fff; }
  .winter-md .wm-h1 { font-size: 15px; }
  .winter-md .wm-h2 { font-size: 14px; }
  .winter-md .wm-h3 { font-size: 13.5px; }
  .winter-md table {
    border-collapse: collapse; margin: 6px 0 8px; width: 100%;
    font-size: 12.5px; display: block; overflow-x: auto;
  }
  .winter-md th, .winter-md td {
    border: 1px solid rgba(255,255,255,0.12); padding: 4px 8px; text-align: left;
    white-space: nowrap;
  }
  .winter-md th { background: rgba(160,198,240,0.10); font-weight: 700; }
`;

export default function AskWinter({ authToken, apiBase }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.sessionStorage.getItem(WINTER_THREAD_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Live streaming state for the in-flight reply: text grows delta by
  // delta; tools collects activity chips ({name, label, done}).
  const [streamText, setStreamText] = useState('');
  const [streamTools, setStreamTools] = useState([]);
  const [error, setError] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  // Pin-to-bottom: only autoscroll if the user hasn't scrolled up.
  const stickToBottomRef = useRef(true);

  // Persist the thread (skip the seeded welcome-only state).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (messages.length <= 1) window.sessionStorage.removeItem(WINTER_THREAD_KEY);
      else window.sessionStorage.setItem(WINTER_THREAD_KEY, JSON.stringify(messages.slice(-40)));
    } catch (_) { /* quota / private mode — non-fatal */ }
  }, [messages]);

  // Listen for the global "open Winter" event (Help menu in the top
  // bar fires it) — loose contract, no prop drilling.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onOpen = () => setOpen(true);
    window.addEventListener('trailercast:open-winter', onOpen);
    return () => window.removeEventListener('trailercast:open-winter', onOpen);
  }, []);

  // Auto-scroll on new content unless the user scrolled up to read.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, streamText, streamTools, sending]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  // First open — seed the welcome message.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: WINTER_WELCOME, ts: Date.now() }]);
    }
    if (open) {
      // Focus the composer as the panel opens.
      setTimeout(() => inputRef.current && inputRef.current.focus(), 80);
    }
  }, [open, messages.length]);

  // First-login coachmark (once per browser).
  useEffect(() => {
    if (!authToken) return undefined;
    try {
      if (!localStorage.getItem(WINTER_INTRO_SEEN_KEY)) {
        const t = setTimeout(() => setShowIntro(true), 1200);
        return () => clearTimeout(t);
      }
    } catch (_) { /* private mode — show anyway */ }
    return undefined;
  }, [authToken]);

  useEffect(() => {
    if (open && showIntro) dismissIntro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismissIntro = () => {
    setShowIntro(false);
    try { localStorage.setItem(WINTER_INTRO_SEEN_KEY, '1'); } catch (_) {}
  };

  const newChat = () => {
    if (sending) stop();
    setMessages([{ role: 'assistant', content: WINTER_WELCOME, ts: Date.now() }]);
    setError(null);
    setInput('');
    try { window.sessionStorage.removeItem(WINTER_THREAD_KEY); } catch (_) {}
    if (inputRef.current) inputRef.current.focus();
  };

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const send = useCallback(async (overrideText) => {
    const text = (overrideText !== undefined ? overrideText : input).trim();
    if (!text || sending) return;
    setError(null);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    const next = [...messages, { role: 'user', content: text, ts: Date.now() }];
    setMessages(next);
    setSending(true);
    setStreamText('');
    setStreamTools([]);
    stickToBottomRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    // Page-context detection: attach the call/demo the user is looking
    // at so "what did they say about X" needs no explanation.
    const ctxPayload = (() => {
      if (typeof window === 'undefined') return {};
      const hash = window.location.hash || '';
      const callMatch = hash.match(/^#\/calls\/([^/?#]+)/);
      if (callMatch) return { callContext: { callId: decodeURIComponent(callMatch[1]) } };
      const demoMatch = hash.match(/^#demo=([^/?#&]+)/);
      if (demoMatch) return { demoContext: { demoId: decodeURIComponent(demoMatch[1]) } };
      return {};
    })();

    let acc = '';
    try {
      const result = await streamWinterReply({
        apiBase,
        authToken,
        signal: controller.signal,
        body: {
          message: text,
          history: next
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .map((m) => ({ role: m.role, content: m.content })),
          ...ctxPayload,
        },
        onDelta: (chunk) => {
          acc += chunk;
          setStreamText(acc);
          // A delta means the current batch of tools finished.
          setStreamTools((prev) => (prev.some((t) => !t.done)
            ? prev.map((t) => ({ ...t, done: true }))
            : prev));
        },
        onTool: (tool) => {
          setStreamTools((prev) => [
            ...prev.map((t) => ({ ...t, done: true })),
            { ...tool, done: false },
          ]);
        },
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: result.reply || '(no reply)', ts: Date.now() }]);
    } catch (err) {
      if (err && err.name === 'AbortError') {
        // User pressed Stop — keep whatever streamed as the reply.
        if (acc.trim()) {
          setMessages((prev) => [...prev, { role: 'assistant', content: acc, ts: Date.now() }]);
        } else {
          setMessages((prev) => prev.slice(0, -1));
          setInput(text);
        }
      } else {
        setError(err.message || 'Something went wrong.');
        // Roll back the user message so they can retry cleanly.
        setMessages((prev) => prev.slice(0, -1));
        setInput(text);
      }
    } finally {
      setSending(false);
      setStreamText('');
      setStreamTools([]);
      abortRef.current = null;
    }
  }, [input, sending, messages, apiBase, authToken]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Auto-grow the composer (up to ~5 lines).
  const onInputChange = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  };

  if (!authToken) return null;

  const showQuickPrompts = messages.length <= 1 && !sending;

  return (
    <>
      {(open || showIntro) && <style>{WINTER_STYLES}</style>}

      {/* First-login coachmark — introduces Winter once, never again. */}
      {!open && showIntro && (
        <div
          role="dialog"
          aria-label="Meet Winter"
          style={{
            position: 'fixed',
            bottom: 88,
            right: 100,
            width: 320,
            maxWidth: 'calc(100vw - 120px)',
            background: '#14141c',
            color: '#e8ecf3',
            padding: 16,
            borderRadius: 14,
            border: '1px solid rgba(160,198,240,0.35)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 0 4px rgba(160,198,240,0.12)',
            zIndex: 9997,
            animation: 'winterIntroFade 260ms ease-out',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 24,
              right: -8,
              width: 16,
              height: 16,
              background: '#14141c',
              borderRight: '1px solid rgba(160,198,240,0.35)',
              borderTop: '1px solid rgba(160,198,240,0.35)',
              transform: 'rotate(45deg)',
            }}
          />

          <button
            type="button"
            onClick={dismissIntro}
            aria-label="Dismiss"
            style={{
              position: 'absolute',
              top: 6,
              right: 8,
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              fontSize: 18,
              lineHeight: 1,
              padding: 4,
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
              <SamoyedAvatar size={36} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Meet Winter</div>
          </div>

          <p style={{ margin: '0 0 10px', fontSize: 13.5, lineHeight: 1.55, color: '#d0d5e0' }}>
            He&apos;s your in-app TrailerCast expert. Ask him <strong>how-to questions</strong>, <strong>data questions</strong> (&ldquo;who did the most demos this month?&rdquo;) — or search <strong>what was said on your calls</strong> (&ldquo;which calls mentioned pricing?&rdquo;). He can pull it all, live.
          </p>

          <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.5, color: '#9ca3af', fontStyle: 'italic' }}>
            Make Winter your first stop for anything you&apos;re stuck on.
          </p>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                dismissIntro();
                setOpen(true);
              }}
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent',
                color: '#e8ecf3',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try him now
            </button>
            <button
              type="button"
              onClick={dismissIntro}
              style={{
                padding: '7px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#6366f1',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Floating action button — bottom-right, above the Feedback pill. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ask Winter"
          style={{
            position: 'fixed',
            bottom: 88,
            right: 20,
            width: 64,
            height: 64,
            borderRadius: '50%',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            background: '#a0c6f0',
            boxShadow: '0 10px 30px rgba(0,0,0,0.35), 0 0 0 4px rgba(160,198,240,0.25)',
            zIndex: 9998,
            transition: 'transform 150ms ease',
            animation: showIntro ? 'winterBubblePulse 2s ease-in-out infinite' : 'none',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.06)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          <SamoyedAvatar size={64} />
          <span
            style={{
              position: 'absolute',
              right: 72,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(20,20,28,0.92)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            Ask Winter
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="winter-panel"
          role="dialog"
          aria-label="Ask Winter chat"
          style={{
            position: 'fixed',
            bottom: 88,
            right: 20,
            width: 400,
            maxWidth: 'calc(100vw - 32px)',
            height: 640,
            maxHeight: 'calc(100vh - 110px)',
            background: 'linear-gradient(180deg, #16161f 0%, #101018 100%)',
            color: '#e8ecf3',
            borderRadius: 18,
            border: '1px solid rgba(160,198,240,0.16)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9998,
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '13px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              background: 'linear-gradient(135deg, rgba(160,198,240,0.18), rgba(139,92,246,0.14))',
              flexShrink: 0,
            }}
          >
            <div style={{ position: 'relative', width: 40, height: 40, marginRight: 11, flexShrink: 0 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden' }}>
                <SamoyedAvatar size={40} />
              </div>
              {/* Online dot */}
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#34d399',
                  border: '2px solid #16161f',
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>Winter</div>
              <div style={{ fontSize: 11.5, opacity: 0.65, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Knows the product · reads your data live
              </div>
            </div>
            <button
              type="button"
              onClick={newChat}
              title="Start a new chat"
              aria-label="New chat"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.14)',
                color: 'rgba(255,255,255,0.75)',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 8,
                padding: '5px 10px',
                marginRight: 8,
                fontFamily: 'inherit',
              }}
            >
              New chat
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                fontSize: 22,
                lineHeight: 1,
                padding: 4,
              }}
            >
              ×
            </button>
          </div>

          {/* Message area */}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="winter-scroll"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px 14px 6px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {messages.map((m, i) => (
              <WinterMessage key={`${m.ts || i}-${i}`} role={m.role} content={m.content} ts={m.ts} />
            ))}

            {/* In-flight reply: tool chips + streaming text + caret */}
            {sending && (
              <div className="winter-row" style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', marginRight: 8, flexShrink: 0, alignSelf: 'flex-end' }}>
                  <SamoyedAvatar size={28} />
                </div>
                <div style={{ maxWidth: '82%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {streamTools.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {streamTools.map((t, i) => (
                        <span key={`${t.name}-${i}`} className="winter-tool-chip">
                          {t.done ? <span aria-hidden="true">✓</span> : <span className="spin" aria-hidden="true" />}
                          {t.label}
                        </span>
                      ))}
                    </div>
                  )}
                  <div
                    style={{
                      padding: '9px 12px',
                      borderRadius: 14,
                      borderBottomLeftRadius: 5,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    {streamText ? (
                      <>
                        <WinterMarkdown text={streamText} />
                        <span className="winter-caret" aria-hidden="true" />
                      </>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 13, fontStyle: 'italic' }}>
                        {streamTools.some((t) => !t.done) ? 'Working on it…' : 'Thinking…'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Quick prompts — first open only */}
            {showQuickPrompts && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 2 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', padding: '2px 2px 0' }}>
                  Try asking
                </div>
                {WINTER_QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => send(p)}
                    style={{
                      textAlign: 'left',
                      padding: '9px 12px',
                      borderRadius: 11,
                      border: '1px solid rgba(160,198,240,0.18)',
                      background: 'rgba(160,198,240,0.05)',
                      color: '#dbe4f0',
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      fontFamily: 'inherit',
                      transition: 'background 120ms ease, border-color 120ms ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(160,198,240,0.12)';
                      e.currentTarget.style.borderColor = 'rgba(160,198,240,0.35)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(160,198,240,0.05)';
                      e.currentTarget.style.borderColor = 'rgba(160,198,240,0.18)';
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}

            {error && (
              <div
                style={{
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  color: '#fca5a5',
                  padding: '9px 12px',
                  borderRadius: 10,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={!input.trim()}
                  style={{
                    flex: 'none',
                    padding: '4px 10px',
                    borderRadius: 7,
                    border: '1px solid rgba(252,165,165,0.4)',
                    background: 'transparent',
                    color: '#fca5a5',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: input.trim() ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                  }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Composer */}
          <div
            style={{
              padding: '10px 12px 12px',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 8,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 13,
                padding: '7px 8px 7px 12px',
              }}
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                placeholder="Ask about your calls, deals, or the product…"
                rows={1}
                style={{
                  flex: 1,
                  resize: 'none',
                  padding: '4px 0',
                  border: 'none',
                  background: 'transparent',
                  color: '#e8ecf3',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  outline: 'none',
                  maxHeight: 110,
                  lineHeight: 1.45,
                }}
              />
              {sending ? (
                <button
                  type="button"
                  onClick={stop}
                  aria-label="Stop generating"
                  title="Stop"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255,255,255,0.08)',
                    color: '#e8ecf3',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ width: 11, height: 11, background: '#e8ecf3', borderRadius: 2, display: 'block' }} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={!input.trim()}
                  aria-label="Send"
                  title="Send (Enter)"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    border: 'none',
                    background: input.trim() ? '#6366f1' : 'rgba(99,102,241,0.35)',
                    color: '#fff',
                    cursor: input.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background 120ms ease',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              )}
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 7 }}>
              Winter can read your company&apos;s data & call transcripts · answers may need a double-check
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WinterMessage({ role, content }) {
  const isUser = role === 'user';
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) { /* clipboard blocked — ignore */ }
  };

  if (isUser) {
    return (
      <div className="winter-row" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          style={{
            maxWidth: '80%',
            padding: '9px 13px',
            borderRadius: 14,
            borderBottomRightRadius: 5,
            background: 'linear-gradient(135deg, #6366f1, #7c5cf3)',
            color: '#fff',
            fontSize: 13.5,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
          }}
        >
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="winter-row" style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', marginRight: 8, flexShrink: 0, alignSelf: 'flex-end' }}>
        <SamoyedAvatar size={28} />
      </div>
      <div
        className="winter-msg-wrap"
        style={{
          position: 'relative',
          maxWidth: '82%',
          padding: '9px 12px',
          borderRadius: 14,
          borderBottomLeftRadius: 5,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <button type="button" className="winter-copy-btn" onClick={copy} aria-label="Copy reply">
          {copied ? '✓ Copied' : 'Copy'}
        </button>
        <WinterMarkdown text={content} />
      </div>
    </div>
  );
}
